import React, {useRef} from 'react'
import PropTypes from 'prop-types'
import {Box, Text, useStyleConfig, useMediaQuery} from '@chakra-ui/react'
import {Navigation, Pagination, Keyboard} from 'swiper/modules'
import {Swiper, SwiperSlide} from 'swiper/react'
import PromoSlideCard from './partials/promo-slide-card'
import {ChevronRight, ChevronLeft} from '../../icons/arrows'
import {remCalc} from '../../../theme/_utils'

export const PromoSlider = ({heading, sliderGroup, uid}) => {
    const swiperStyles = useStyleConfig('Swiper')
    const styles = useStyleConfig('PromoSlider')
    const swiperRef = useRef(null)

    const [isSmallTablet] = useMediaQuery(
        `(min-width: ${remCalc(450)}) and (max-width: ${remCalc(767)})`,
    )

    const slidesToView = {
        base: 1,
        sm: 1.2,
        md: 1.3,
        lg: 1.7,
        xl: 1.9,
    }

    if (!sliderGroup || sliderGroup?.length === 0) return null

    // swiperjs doesn't work correctly with 3 slides when loop and centeredSlides are enabled/
    // I add duplicates and hide the bullets from the fake slide
    const slides = [...sliderGroup]
    const needsFakeSlides = sliderGroup.length === 3
    const allSlides = needsFakeSlides
        ? [...slides, ...slides.map((slide) => ({...slide, isFake: true}))]
        : slides

    return (
        <Box
            __css={{...swiperStyles, ...styles.container}}
            data-testid="promo-slider-container"
            id={`promo-slider-image-${uid}`}
        >
            {heading && (
                <Text as="h2" {...styles.heading}>
                    {heading}
                </Text>
            )}
            <Swiper
                modules={[Navigation, Pagination, Keyboard]}
                navigation={{
                    prevEl: `.swiper-button-prev`,
                    nextEl: `.swiper-button-next`,
                }}
                pagination={{
                    clickable: true,
                    renderBullet: function (index, className) {
                        // only show bullets for the original slide count
                        if (index < slides.length) {
                            return `<span class="${className}"></span>`
                        }
                        return ''
                    },
                }}
                keyboard={{
                    enabled: true,
                }}
                // mousewheel={true}
                loop={true}
                centeredSlides={true}
                tabIndex={0}
                onInit={(swiper) => {
                    swiperRef.current = swiper
                }}
                onSlideChange={(swiper) => {
                    if (!needsFakeSlides) return
                    // calculate which bullet point should be active
                    const realIndex = swiper.realIndex % slides.length

                    // here we need to include the id in the selector because there can be multiple promo sliders on the page
                    const bullets = document.querySelectorAll(
                        `#promo-slider-image-${uid} .swiper-pagination-bullet`,
                    )
                    bullets.forEach((bullet, idx) => {
                        if (idx === realIndex) {
                            bullet.classList.add('swiper-pagination-bullet-active')
                        } else {
                            bullet.classList.remove('swiper-pagination-bullet-active')
                        }
                    })
                }}
                breakpoints={{
                    0: {
                        slidesPerView: slidesToView.base,
                        spaceBetween: 0,
                    },
                    450: {
                        slidesPerView: slidesToView.sm,
                        spaceBetween: 16,
                    },
                    768: {
                        slidesPerView: slidesToView.md,
                        spaceBetween: 16,
                    },
                    1024: {
                        slidesPerView: slidesToView.lg,
                        spaceBetween: 24,
                    },
                    1200: {
                        slidesPerView: slidesToView.xl,
                        spaceBetween: 142,
                    },
                }}
            >
                {allSlides.map((slider, index) => (
                    <SwiperSlide key={index} data-fake={slider.isFake ? 'true' : 'false'}>
                        <PromoSlideCard
                            title={slider?.text_block?.title}
                            bodyCopy={slider?.text_block?.body_copy}
                            button={slider?.text_block?.button}
                            image={slider?.image}
                            url={slider?.media?.url}
                            alt={slider?.media?.description}
                        />
                    </SwiperSlide>
                ))}

                <ChevronLeft
                    className="swiper-button-prev"
                    aria-label="swiper button prev icon"
                    tabIndex={0}
                    sx={styles.chevronIcon}
                    left={isSmallTablet ? '6.5%' : {base: '4%', md: '10%', lg: '19%', xl: '21%'}}
                />
                <ChevronRight
                    className="swiper-button-next"
                    aria-label="swiper button next icon"
                    tabIndex={0}
                    sx={styles.chevronIcon}
                    right={isSmallTablet ? '6.5%' : {base: '4%', md: '10%', lg: '19%', xl: '21%'}}
                />
                <Box className="promo-slider-pagination" />
            </Swiper>
        </Box>
    )
}

PromoSlider.propTypes = {
    heading: PropTypes.string,
    uid: PropTypes.string,
    sliderGroup: PropTypes.arrayOf(
        PropTypes.shape({
            text_block: PropTypes.shape({
                title: PropTypes.string,
                body_copy: PropTypes.string,
                cta: PropTypes.shape({title: PropTypes.string, href: PropTypes.string}),
                is_external_url: PropTypes.bool,
            }),
            image: PropTypes.object,
            media: PropTypes.shape({
                url: PropTypes.string,
                alt: PropTypes.string,
            }),
        }),
    ),
}
